import { type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAdminApp } from "@/hooks/useAdminApp";
import { usePersistentSidebarState } from "@/hooks/usePersistentSidebarState";
import { useHasGroup } from "@/hooks/useHasGroup";
import { useStateMachineValue } from "@/core/arch/state_machine";
import { ROUTES } from "@/routes";
import { ChevronsUpDown, Gamepad2, LogOut, Search } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/base/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/base/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/base/sidebar";
import { Separator } from "@/components/ui/base/separator";
import {
  BreadcrumbsProvider,
  HeaderBreadcrumbs,
} from "@/presentation/layout/breadcrumbs";
import { ThemeToggle } from "@/presentation/layout/ThemeToggle";
import {
  FOOTER_NAV_ITEMS,
  NAV_SECTIONS,
  visibleNavItems,
} from "@/presentation/layout/nav_items";
import { CommandPalette } from "@/presentation/search/CommandPalette";
import { useCommandPalette } from "@/presentation/search/useCommandPalette";

function NavUser({ email, onLogout }: { email: string; onLogout: () => void }) {
  const { isMobile } = useSidebar();
  const initials = email.split("@")[0].slice(0, 2).toUpperCase();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="rounded-lg">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {email.split("@")[0]}
                </span>
                <span className="truncate text-xs">{email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {email.split("@")[0]}
                  </span>
                  <span className="truncate text-xs">{email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to={ROUTES.PLAY_HOOPS}>
                  <Gamepad2 />
                  Play CPOS Hoops
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={onLogout}>
                <LogOut />
                Log out
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AdminLayout({ children }: { children: ReactNode }) {
  const { adminUserBloc } = useAdminApp();
  const [state] = useStateMachineValue(adminUserBloc);
  const [sidebarOpen, setSidebarOpen] = usePersistentSidebarState();
  const isDeveloper = useHasGroup("CPOS-Developers");
  const { open: paletteOpen, setOpen: setPaletteOpen } = useCommandPalette();

  const groups = isDeveloper ? ["CPOS-Developers"] : [];

  return (
    <BreadcrumbsProvider>
      <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <Sidebar collapsible="icon">
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton size="lg" asChild>
                  <Link to="/">
                    <div className="flex aspect-square size-8 items-center justify-center rounded-lg">
                      <img
                        src="/favicon.png"
                        alt="CPOS"
                        className="size-8 rounded-lg"
                      />
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">CPOS Admin</span>
                      <span className="truncate text-xs">Console</span>
                    </div>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>

          <SidebarContent>
            {NAV_SECTIONS.map((section) => {
              const items = visibleNavItems(section.items, groups);
              if (items.length === 0) return null;
              return (
                <SidebarGroup key={section.label ?? "overview"}>
                  {section.label && (
                    <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
                  )}
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {items.map((item) => (
                        <SidebarMenuItem key={item.to}>
                          <SidebarMenuButton asChild tooltip={item.label}>
                            <NavLink to={item.to} end={item.end}>
                              <item.icon />
                              <span>{item.label}</span>
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              );
            })}
          </SidebarContent>

          <SidebarFooter>
            <SidebarMenu>
              {FOOTER_NAV_ITEMS.map((item) => (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton asChild tooltip={item.label}>
                    <NavLink to={item.to}>
                      <item.icon />
                      <span>{item.label}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
            <NavUser
              email={state.email ?? ""}
              onLogout={() => adminUserBloc.logout()}
            />
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>

        <SidebarInset>
          <header className="bg-background sticky top-0 z-10 flex h-16 shrink-0 items-center gap-2 border-b px-4 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="mr-2 h-4" />
              <HeaderBreadcrumbs />
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                aria-label="Search"
                className="text-muted-foreground hover:bg-accent hover:text-accent-foreground mr-1 flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm"
              >
                <Search className="h-4 w-4" />
                <span className="hidden sm:inline">Search</span>
                <kbd className="bg-muted hidden rounded px-1.5 py-0.5 font-mono text-[10px] sm:inline">
                  ⌘K
                </kbd>
              </button>
              <ThemeToggle />
            </div>
          </header>
          <main className="min-w-0 flex-1 overflow-y-auto p-8">{children}</main>
          <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        </SidebarInset>
      </SidebarProvider>
    </BreadcrumbsProvider>
  );
}
